import * as React from 'react';
import { observableValue } from '@vscode/observables';
import { viewWithModel, ViewModel, prop } from '@vscode/observables-react';
import { resolveViewport } from '../core/index.js';
import { ComponentRendering } from '../core/ComponentRenderer.js';
import type { FixtureItem } from './ExplorerModel.js';
import { buildQueryParamUrl } from '../lib/queryParamObservable.js';
import { getExplorerShadowHost } from '../lib/explorerShadowHost.js';
import { registerFixtureContainer } from '../lib/fixtureFocusTrap.js';

let _nextSlotId = 0;

class FixturePreviewItemModel extends ViewModel({
  fixture: prop.const<FixtureItem>(),
  compact: prop.const<boolean | undefined>(),
  onSelect: prop.const<((nodeId: string) => void) | undefined>(),
}) {
  private _canvasRef: HTMLDivElement | null = null;
  private _fixtureContainer: HTMLDivElement | null = null;
  private _rendering: ComponentRendering | null = null;
  private _resizeObserver: ResizeObserver | null = null;

  readonly measuredWidth = observableValue<number | undefined>(this, undefined);
  readonly measuredHeight = observableValue<number | undefined>(this, undefined);
  readonly slotName = `fixture-${_nextSlotId++}`;

  get fixture(): FixtureItem { return this.props.fixture; }
  get compact(): boolean { return this.props.compact ?? false; }
  get onSelect(): ((nodeId: string) => void) | undefined { return this.props.onSelect; }

  setCanvasRef = (el: HTMLDivElement | null): void => {
    if (el === this._canvasRef) return;

    this._cleanup();

    this._canvasRef = el;
    if (!el) return;

    const shadowHost = getExplorerShadowHost();

    const fixtureContainer = document.createElement('div');
    fixtureContainer.slot = this.slotName;

    const component = this.fixture.component;
    const displayMode = component.displayMode;
    if (displayMode.type === 'page') {
      const viewport = displayMode.viewports[0];
      const resolved = resolveViewport(viewport);
      fixtureContainer.style.width = `${resolved.width}px`;
      fixtureContainer.style.height = `${resolved.height}px`;
      fixtureContainer.style.overflow = 'auto';
    } else {
      fixtureContainer.style.all = 'initial';
      fixtureContainer.style.display = 'inline-block';
    }

    shadowHost.appendChild(fixtureContainer);
    this._fixtureContainer = fixtureContainer;
    registerFixtureContainer(fixtureContainer);

    const rendering = new ComponentRendering(fixtureContainer, component);
    this._rendering = rendering;

    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (height > 0) {
          this.measuredWidth.set(Math.round(width), undefined);
          this.measuredHeight.set(Math.round(height), undefined);
        }
      }
    });
    this._resizeObserver.observe(rendering.renderTarget);
  };

  private _cleanup(): void {
    this._rendering?.dispose();
    this._rendering = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._fixtureContainer?.remove();
    this._fixtureContainer = null;
  }

  dispose(): void {
    this._cleanup();
  }
}

export const FixturePreviewItem = viewWithModel(
  FixturePreviewItemModel,
  {},
  (reader, model) => {
    const fixture = model.fixture;
    const component = fixture.component;
    const measuredWidth = model.measuredWidth.read(reader);
    const measuredHeight = model.measuredHeight.read(reader);
    const compact = model.compact;

    const dimensionsText = measuredWidth && measuredHeight
      ? `${measuredWidth} × ${measuredHeight}`
      : '';

    const wrapperStyle = compact ? previewItemStyles.wrapperCompact : previewItemStyles.wrapper;
    const nameStyle = compact ? previewItemStyles.nameCompact : previewItemStyles.name;

    return (
      <div style={wrapperStyle}>
        <div style={previewItemStyles.labelRow}>
          {compact && (
            <a
              href={buildQueryParamUrl('fixture', fixture.id)}
              style={{ ...nameStyle, textDecoration: 'none', color: 'inherit' }}
              onClick={(e) => { if (model.onSelect) { e.preventDefault(); model.onSelect(fixture.id); } }}
            >{fixture.name}</a>
          )}
          {!compact && dimensionsText && (
            <span style={previewItemStyles.dimensions}>{dimensionsText}</span>
          )}
        </div>
        <div ref={model.setCanvasRef} style={getCanvasStyle(component.background)}>
          <slot name={model.slotName} />
        </div>
      </div>
    );
  }
);

// Checkered pattern for transparency background (Figma-style)
const CHECKER_SIZE = 8;

function createCheckerPattern(baseColor: string, checkerColor: string): string {
  return `
    linear-gradient(45deg, ${checkerColor} 25%, transparent 25%),
    linear-gradient(-45deg, ${checkerColor} 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, ${checkerColor} 75%),
    linear-gradient(-45deg, transparent 75%, ${checkerColor} 75%)
  `;
}

const lightPattern = {
  backgroundColor: '#ffffff',
  backgroundImage: createCheckerPattern('#ffffff', '#e5e5e5'),
};

const darkPattern = {
  backgroundColor: '#1e1e1e',
  backgroundImage: createCheckerPattern('#1e1e1e', '#2d2d2d'),
};

const canvasBaseStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '12px',
  borderRadius: '4px',
  backgroundSize: `${CHECKER_SIZE * 2}px ${CHECKER_SIZE * 2}px`,
  backgroundPosition: `0 0, 0 ${CHECKER_SIZE}px, ${CHECKER_SIZE}px -${CHECKER_SIZE}px, -${CHECKER_SIZE}px 0`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  position: 'relative',
};

function getCanvasStyle(background: 'light' | 'dark'): React.CSSProperties {
  const pattern = background === 'dark' ? darkPattern : lightPattern;
  return {
    ...canvasBaseStyle,
    backgroundColor: pattern.backgroundColor,
    backgroundImage: pattern.backgroundImage,
  };
}

const previewItemStyles = {
  wrapper: {
    marginBottom: '32px',
  } as React.CSSProperties,
  wrapperCompact: {
    marginBottom: '0',
  } as React.CSSProperties,
  labelRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    marginBottom: '8px',
  } as React.CSSProperties,
  name: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--component-explorer-foreground)',
  } as React.CSSProperties,
  nameCompact: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--component-explorer-descriptionForeground)',
  } as React.CSSProperties,
  dimensions: {
    fontSize: '11px',
    color: 'var(--component-explorer-descriptionForeground)',
    fontFamily: 'var(--component-explorer-editor-font-family, monospace)',
  } as React.CSSProperties,
};
